/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Accordion from './Accordion';
import LayoutContainer from './LayoutContainer';

import icCoins from '@/assets/ic_coins2.svg';
import { Button } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import { AccordionItem, FaqSectionProps } from '@/types/faq';

const PinsAndPremiumPack = ({ isPublic, state, title }: FaqSectionProps) => {
  const { isMobile } = useIsDesktop();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    state?.accordionId || null
  );
  const navigate = useNavigate();

  // Actualizar el acordeón activo cuando cambia el state
  useEffect(() => {
    if (state?.accordionId) {
      setActiveAccordion(state.accordionId);
    }
  }, [state]);

  // Manejador para navegar a la edición de cuenta
  const handleAddCreditClick = () => {
    isPublic
      ? navigate(ROUTES.basePath)
      : navigate(ROUTES.myAccount, { state: { scrollToTop: true } });
  };

  const AddCredit = () => (
    <Button
      className="mt-8 mx-auto font-bold"
      type="button"
      variant="text"
      onClick={handleAddCreditClick}
    >
      Cargar saldo
    </Button>
  );

  // Datos para los acordeones
  const accordionItems: AccordionItem[] = [
    {
      id: 'pines-obtener',
      title: '¿Para qué sirven y cómo obtengo pines?',
      content: (
        <>
          <p>
            Los pines son códigos especiales que sirven para realizar
            transferencias. En tu equipo de Fútbol 11, la primera transferencia
            es gratuita. Para realizar el resto es necesario ingresar un Pin por
            cada transferencia que hagas. En Fútbol 5, ingresando un solo Pin
            accedés a transferencias ilimitadas para mejorar tu equipo
          </p>
          <br />
          <p>
            Desde la sección <strong>Mis Pines</strong> del sitio o la APP de
            Gran DT podés acceder a todos los medios de pago para obtener tus
            pines. Podés conseguir desde:
          </p>
          <br />
          <ol className="list-decimal pl-6 mt-4 marker:text-blue-700 marker:font-bold">
            <li className="mb-2">
              Con tus tarjetas de Crédito Visa, American Express o Mastercard.
            </li>
            <li className="mb-2">Con tus tarjetas de Débito Visa o Maestro.</li>
            <li className="mb-2">
              Desde pagomiscuentas.com, eligiendo el producto Gran DT.
            </li>
            <li className="mb-2">En Pago Fácil o Rapipago.</li>
            <li className="mb-2">
              Kioscos que recargan crédito para celulares.
            </li>
            <li className="mb-2">
              Desde linkpagos.com.ar, eligiendo el producto Gran DT.
            </li>
          </ol>
        </>
      ),
    },
    {
      id: 'pines-pack-premium',
      title: '¿Cómo obtengo el Pack Premium?',
      content: (
        <>
          <p>
            El Pack Premium es una membresía que dura todo el Torneo de Gran DT
            y se abona por única vez. Al obtenerlo, te convertís en un DT
            Premium. Esto te permite acceder a diferentes beneficios: Tenés
            transferencias ilimitadas sin ingresar pines. Y si sos el campeón de
            Gran DT multiplicás tu premio y te llevás $8.500.000.
          </p>
          <br />
          <p>
            Para obtener el Pack Premium tenés que acceder a la sección Mis
            Pines, desde tu computadora o celular y seleccionar el Pack. Luego
            deberás elegir el medio de pago, abonar el costo y en el lapso de 72
            hs. te convertirás en un DT Premium.
          </p>
          <br />
          <p>
            También podés cargar saldo en tu cuenta de Gran DT con cualquiera de
            los medios disponibles y luego convertirlo en un Pack Premium.
          </p>
        </>
      ),
    },
    {
      id: 'pines-carga',
      title: '¿Cómo realizo una carga de saldo?',
      content: (
        <>
          <p>
            Podés cargar saldo en tu cuenta de Gran DT y luego convertirlo al
            Pack de Pines que más te convenga. Para eso, tenés que acceder a la
            sección Mis Pines, ir a <AddCredit />, seleccionar el medio de pago
            y el monto a cargar en tu cuenta.
          </p>
          <br />
          <p>
            El saldo se te acreditará dentro de las 72 hs. y podrás convertirlo
            al Pack de pines o al Pin especial que quieras.
          </p>
        </>
      ),
    },
  ];

  // Manejador para alternar acordeones
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <LayoutContainer>
      <div className="flex gap-x-3 items-center content-center mb-8">
        {isMobile && (
          <img
            alt="Gran DT - logo calendario"
            className="w-6 h-6"
            src={icCoins}
          />
        )}
        <span className={`text-lg text-[#333333] font-bold`}>{title}</span>
      </div>

      {accordionItems.map((item) => (
        <Accordion
          key={item.id}
          isOpen={activeAccordion === item.id}
          title={item.title}
          onToggle={() => toggleAccordion(item.id)}
        >
          <div className="text-sm text-gray-600">{item.content}</div>
        </Accordion>
      ))}
    </LayoutContainer>
  );
};

export default PinsAndPremiumPack;
